// src/pages/services/HolisticHealing.jsx
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import leaf from '../../assets/leaf1.png';
import heroImg from '../../assets/Holistic Healing Benefits.png';
import doctorImg from '../../assets/doctors4.png';
import Navbar from '../../common/Navbar';
import Footer from '../../common/Footer';

const HolisticHealing = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-xs md:text-sm leading-loose">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-screen flex flex-col items-center justify-center overflow-hidden bg-gradient-to-b from-[#E8E0D5] to-[#F8F3EA] px-4 sm:px-6">
        <div className="absolute inset-0 bg-black/50 z-10"></div>
        <img 
          src={heroImg} 
          alt="Holistic Healing" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        
        <div className="relative z-20 text-center max-w-6xl mx-auto flex flex-col items-center px-4 sm:px-6">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-playfair font-bold text-white mb-6 sm:mb-8 drop-shadow-3xl leading-tight">
            Holistic Healing
          </h1>
          <p className="text-base md:text-xl lg:text-2xl text-white/95 font-light leading-relaxed max-w-3xl sm:max-w-5xl drop-shadow-2xl">
            True wellness is not just the absence of disease — it is harmony of body, mind, and spirit.
          </p>
        </div>

        {/* Decorative floating leaves */}
        <img src={leaf} className="absolute top-20 left-10 w-28 sm:w-32 opacity-30 animate-float" />
        <img src={leaf} className="absolute bottom-32 right-16 w-36 sm:w-40 opacity-25 animate-float" style={{ animationDelay: '2s' }} />
        <img src={leaf} className="absolute top-1/2 -left-16 sm:-left-20 w-40 sm:w-48 opacity-20 animate-float" style={{ animationDelay: '4s' }} />
      </section>

      {/* Core Pillars */}
      <section className="py-24 px-4 sm:px-6 bg-[#F8F3EA]">
        <div className="max-w-7xl mx-auto flex flex-col items-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-playfair text-[#0B2A2C] mb-10 sm:mb-16">
            The Three Pillars of True Wellness
          </h2>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-8 sm:gap-12 w-full">
            {[
              { icon: "", title: "Body (Sharira)", desc: "Purify, nourish, and strengthen through diet, herbs, and therapies" },
              { icon: "", title: "Mind (Manas)", desc: "Cultivate peace, clarity, and emotional balance through meditation & yoga" },
              { icon: "", title: "Spirit (Atma)", desc: "Connect to your deepest self and the universal consciousness" }
            ].map((pillar, i) => (
              <div 
                key={i}
                className="flex-1 min-w-[220px] max-w-sm bg-white/90 backdrop-blur-sm rounded-3xl p-8 sm:p-12 shadow-2xl hover:shadow-3xl transition-all hover:-translate-y-4  flex flex-col items-center text-center"
              >
                <div className="text-7xl mb-6 opacity-90">{pillar.icon}</div>
                <h3 className="text-2xl md:text-3xl font-bold text-[#0B2A2C] mb-4">{pillar.title}</h3>
                <p className="text-gray-700 leading-relaxed">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey Steps */}
      <section className="py-24 px-4 sm:px-6 bg-[#E4D7C2]">
        <div className="max-w-5xl mx-auto flex flex-col gap-12">
          <h2 className="text-3xl text-center sm:text-4xl md:text-5xl font-playfair text-[#0B2A2C] mb-10 sm:mb-16">
            Your Path to Complete Harmony
          </h2>
          <div className="flex flex-col gap-12">
            {[
              "Personal Consultation & Prakriti Analysis",
              "Customized Healing Protocol (Diet, Herbs, Therapies)",
              "Daily Sadhana: Yoga, Pranayama & Meditation",
              "Emotional Release & Mind-Body Integration",
              "Spiritual Practices & Self-Inquiry",
              "Ongoing Support & Lifestyle Transformation"
            ].map((step, i) => (
              <div key={i} className="flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-12">
                <div className="w-16 h-16 sm:w-24 sm:h-24 bg-[#0B2A2C] text-white rounded-full flex items-center justify-center text-xl sm:text-3xl font-bold shadow-md sm:shadow-2xl flex-shrink-0 mx-auto sm:mx-0">
                  {i + 1}
                </div>
                <div className="flex-col text-center mx-auto flex-1 bg-white items-center rounded-3xl p-8 sm:p-10 shadow-xl border border-[#E4D7C2] text-center sm:text-left">
                  <h4 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0B2A2C] mb-3">
                    {step}
                  </h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-28 bg-gradient-to-r from-[#0B2A2C] via-[#1A4D4F] to-[#0B2A2C] text-white text-center px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col items-center">
          <img 
            src={doctorImg} 
            alt="Dr. V. K. Subrahmanyeswara Rao" 
            className="w-36 h-36 sm:w-40 sm:h-40 rounded-full mb-8 sm:mb-12 border-4 sm:border-8 border-amber-300/30 shadow-2xl"
          />
          <blockquote className="text-xl sm:text-2xl md:text-4xl font-joan italic leading-tight max-w-3xl sm:max-w-6xl mb-6 sm:mb-12">
            “The greatest healing is not in curing disease — <br />
            it is in awakening the healer within.”
          </blockquote>
          <p className="mt-6 sm:mt-12 text-base sm:text-lg md:text-xl opacity-95 font-light">
            — Dr. V. K. Subrahmanyeswara Rao
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 sm:py-32 text-center bg-[#F8F3EA] px-4 sm:px-6 flex flex-col items-center gap-6">
        <h2 className="text-3xl sm:text-4xl md:text-6xl font-playfair text-[#0B2A2C] leading-tight">
          Begin Your Journey to Wholeness
        </h2>
        <p className="text-sm sm:text-base md:text-lg text-gray-700 max-w-md sm:max-w-3xl leading-relaxed">
          This is not just treatment — this is transformation.
        </p>
        <button 
          onClick={() => navigate("/BookNow")}
          className="bg-gradient-to-r from-[#0B2A2C] to-[#1A4D4F] text-white px-12 sm:px-20 py-4 sm:py-8 rounded-full text-lg sm:text-2xl font-bold hover:shadow-3xl hover:scale-105 transition-all duration-500 transform shadow-2xl"
        >
          Start Your Healing Journey
        </button>
      </section>

      <Footer />
    </div>
  );
};

export default HolisticHealing;
